import { isYourStuffAllowed } from "@/utils/isYourStuffAllowed";

/**
 * Normalizes account admin_permission for routing and sidebar checks.
 * "None" / missing / empty → no elevated nav (AI-only shell under idea-MDP-432).
 */
export function normalizeAdminPermission(accessPermission) {
  const raw = accessPermission?.admin_permission;
  if (raw == null || raw === "") {
    return {
      isOwner: false,
      isCoOwner: false,
      isAdmin: false,
      isOwnerOrCoOwner: false,
      hasElevatedNav: false,
    };
  }
  const p = String(raw).toLowerCase().replace(/\s+/g, "");
  const isOwner = p === "owner";
  const isCoOwner = p === "co-owner" || p === "coowner";
  const isAdmin = p === "admin" || p === "administrator";
  return {
    isOwner,
    isCoOwner,
    isAdmin,
    isOwnerOrCoOwner: isOwner || isCoOwner,
    hasElevatedNav: isOwner || isCoOwner || isAdmin,
  };
}

/**
 * Default post-login / "/" redirect. When idea-MDP-432 is on, users without
 * owner/co-owner/admin do not use has_readiness_access to reach /new-readiness
 * (matches AI-only sidebar for "None").
 *
 * @param {Record<string, unknown>} accessPermission
 * @param {{
 *   newYourStuffEnabled: boolean,
 *   showNewOrganizationTab?: boolean,
 *   showNewReadinessTab: boolean,
 *   showPerformanceTab: boolean,
 *   showFairPayTab: boolean,
 *   ideaMDP432: boolean,
 *   skipOrganization?: boolean,
 * }} options
 * @returns {import('vue-router').RawLocation}
 */
export function resolveDefaultLoggedInRedirect(accessPermission, options) {
  const {
    newYourStuffEnabled,
    showNewOrganizationTab = true,
    showNewReadinessTab,
    showPerformanceTab,
    showFairPayTab,
    ideaMDP432,
    skipOrganization = false,
  } = options;

  const { isOwnerOrCoOwner, hasElevatedNav } = normalizeAdminPermission(accessPermission);

  // Your Stuff landing requires isYourStuffAllowed (has_employee + your_stuff_permission).
  if (isYourStuffAllowed(accessPermission) && newYourStuffEnabled) {
    return { path: "/your-stuff/employee/responsibilities" };
  }

  if (ideaMDP432) {
    if (!hasElevatedNav) {
      return { path: "/404" };
    }
    if (!skipOrganization && showNewOrganizationTab && hasElevatedNav) {
      return { name: "NewOrganization", params: { tab: "dashboard" } };
    }
    if (showNewReadinessTab && hasElevatedNav) {
      return { path: "/new-readiness" };
    }
    if (showPerformanceTab && hasElevatedNav) {
      return { path: "/performance" };
    }
    if (showFairPayTab && hasElevatedNav) {
      return { path: "/fair-pay" };
    }
    if (isOwnerOrCoOwner) {
      return { path: "/settings" };
    }
    return { path: "/404" };
  }

  if (!skipOrganization && showNewOrganizationTab && isOwnerOrCoOwner) {
    return { name: "NewOrganization", params: { tab: "dashboard" } };
  }
  const hasReadinessAccess = accessPermission?.has_readiness_access;
  if (showNewReadinessTab && (isOwnerOrCoOwner || hasReadinessAccess)) {
    return { path: "/new-readiness" };
  }
  if (showPerformanceTab) {
    return { path: "/performance" };
  }
  if (showFairPayTab) {
    return { path: "/fair-pay" };
  }
  if (isOwnerOrCoOwner) {
    return { path: "/settings" };
  }
  return { name: "NewOrganization", params: { tab: "dashboard" } };
}
